import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const API_BASE = 'https://api.jjuma.com';
const API_V1 = `${API_BASE}/api/v1`;

const DOC_SECTIONS = [
  ['introduction',    'Introduction'],
  ['getting-started', 'Getting Started'],
  ['authentication',  'Authentication'],
  ['api-endpoints',   'API Endpoints'],
  ['create-payment',  'Create Payment'],
  ['verify-payment',  'Verify Payment'],
  ['transactions',    'List Transactions'],
  ['payment-links',   'Payment Links'],
  ['checkout',        'Checkout'],
  ['webhooks',        'Webhooks'],
  ['woocommerce',     'WooCommerce'],
  ['error-codes',     'Error Codes'],
  ['testing',         'Testing'],
  ['going-live',      'Going Live'],
];

/* ─── Code block ─────────────────────────────────────────────────────────── */
function CodeBlock({ lang, code }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative rounded-xl overflow-hidden border border-slate-700/60 my-4 shadow-md">
      <div className="flex items-center justify-between bg-[#0f172a] px-4 py-2.5">
        <span className="text-xs text-emerald-400 font-mono font-semibold tracking-wide uppercase">{lang}</span>
        <button
          onClick={copy}
          className="text-xs text-slate-400 hover:text-emerald-300 transition-colors font-medium px-2 py-0.5 rounded-md border border-slate-700 hover:border-emerald-500/50"
        >
          {copied ? '✓ Copied' : 'Copy'}
        </button>
      </div>
      <pre className="bg-[#1e293b] p-4 overflow-x-auto text-sm">
        <code className="text-slate-200 font-mono whitespace-pre leading-relaxed">{code}</code>
      </pre>
    </div>
  );
}

/* ─── Badge helpers ──────────────────────────────────────────────────────── */
function MethodBadge({ method }) {
  const colors = {
    GET:  'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    POST: 'bg-violet-500/15 text-violet-400 border-violet-500/30',
  };
  return (
    <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-bold font-mono mr-2 ${colors[method] || 'bg-slate-500/15 text-slate-400 border-slate-500/30'}`}>
      {method}
    </span>
  );
}

function KeyBadge({ label, color = 'amber' }) {
  const cls = {
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    violet: 'bg-violet-50 text-violet-800 border-violet-200',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${cls[color]}`}>
      {label}
    </span>
  );
}

/* ─── Section wrapper ────────────────────────────────────────────────────── */
function Section({ id, title, badge, children }) {
  return (
    <section id={id} className="scroll-mt-24 mb-10">
      <div className="flex items-center gap-3 mb-4">
        <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {title}
        </h3>
        {badge}
      </div>
      <div className="text-sm text-slate-600 leading-relaxed space-y-4">{children}</div>
    </section>
  );
}

function InfoBox({ color = 'emerald', title, children }) {
  const cls = {
    emerald: 'border-emerald-200 bg-emerald-50/80 text-emerald-900',
    amber:   'border-amber-200 bg-amber-50/80 text-amber-900',
    red:     'border-red-200 bg-red-50/80 text-red-900',
    slate:   'border-slate-200 bg-slate-50 text-slate-800',
    violet:  'border-violet-200 bg-violet-50/80 text-violet-900',
  };
  return (
    <div className={`rounded-xl border p-4 ${cls[color]}`}>
      {title && <p className="font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</p>}
      {children}
    </div>
  );
}

function EndpointCard({ method, path, description, requiresKey }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <MethodBadge method={method} />
        <code className="text-sm font-mono text-slate-800 break-all">{path}</code>
      </div>
      {description && <p className="text-xs text-slate-500 mb-2">{description}</p>}
      {requiresKey && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {requiresKey.map(k => (
            <KeyBadge key={k.label} label={k.label} color={k.color} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Content sections ───────────────────────────────────────────────────── */
function IntroductionSection() {
  return (
    <Section id="introduction" title="Introduction">
      <p>
        Jjuma Pay is a hosted checkout and payments API for creators, freelancers, churches, NGOs,
        and growing businesses. Accept payments through branded Jjuma checkout pages with a
        simple integration path — no PCI compliance required.
      </p>
      <div className="rounded-xl border border-slate-200 bg-slate-900 p-5">
        <p className="text-xs uppercase tracking-wider text-emerald-400 font-semibold mb-1">Base URL</p>
        <code className="text-emerald-300 font-mono text-sm">{API_V1}</code>
      </div>
      <InfoBox color="slate" title="Test Mode vs Live Mode">
        <ul className="space-y-2 text-sm">
          <li><strong>Test Mode:</strong> Use API keys with the <code className="bg-slate-200 px-1 rounded text-xs">bp_test_</code> prefix. No real money moves. Available for all accounts.</li>
          <li><strong>Live Mode:</strong> Use API keys with the <code className="bg-slate-200 px-1 rounded text-xs">bp_live_</code> prefix. Requires a verified, approved business account with live mode enabled.</li>
        </ul>
      </InfoBox>
      <InfoBox color="slate" title="Account Types">
        <ul className="space-y-2 text-sm">
          <li><strong>Individual accounts:</strong> Can create payment links after approval. API access is not available.</li>
          <li><strong>Business accounts:</strong> Full API access, webhooks, and developer tools after business KYC verification and admin approval.</li>
        </ul>
        <p className="text-xs mt-2 text-slate-500 font-medium">⚠ API access is available for verified business accounts only.</p>
      </InfoBox>
    </Section>
  );
}

function GettingStartedSection() {
  return (
    <Section id="getting-started" title="Getting Started">
      <p>Create an account, complete business verification, then generate API keys from the dashboard.</p>
      <InfoBox color="emerald" title="Recommended setup flow">
        <ol className="list-decimal pl-5 space-y-1.5 text-sm">
          <li>Create your account at <a href="https://dashboard.jjuma.com/register" className="underline font-medium" target="_blank" rel="noreferrer">dashboard.jjuma.com/register</a></li>
          <li>Verify your email address</li>
          <li>Complete KYC / business verification</li>
          <li>Wait for admin approval (business accounts)</li>
          <li>Generate an API key from the <strong>API Keys</strong> page</li>
          <li>Create your first payment using the API</li>
          <li>Test the flow end-to-end in test mode</li>
          <li>Go live with your production keys</li>
        </ol>
      </InfoBox>
    </Section>
  );
}

function AuthenticationSection() {
  return (
    <Section id="authentication" title="Authentication">
      <p>
        All API requests must include an API key. Jjuma Pay uses two types of keys — public keys
        for browser-facing requests, and secret keys for secure server-side requests.
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        <InfoBox color="emerald" title="Public Key">
          <code className="text-xs font-mono bg-emerald-100 px-1 rounded">bp_live_pub_...</code>
          <p className="text-xs mt-1">Safe to use in HTML forms and frontend JavaScript. Use for <code className="text-xs">/payments/create</code> and <code className="text-xs">/payment-links/slug</code>.</p>
        </InfoBox>
        <InfoBox color="violet" title="Secret Key">
          <code className="text-xs font-mono bg-violet-100 px-1 rounded">bp_live_sec_...</code>
          <p className="text-xs mt-1">Keep on your server only. Required for <code className="text-xs">/transactions</code> and <code className="text-xs">/payments/verify</code>.</p>
        </InfoBox>
      </div>
      <CodeBlock
        lang="http"
        code={`Authorization: Bearer YOUR_API_KEY\nContent-Type: application/json`}
      />
      <p>Or pass the key as a form field <code className="bg-slate-100 px-1 rounded text-xs">api_key</code> in HTML form integrations.</p>
      <CodeBlock
        lang="cURL"
        code={`curl -X GET ${API_V1}/transactions \\\n  -H "Authorization: Bearer bp_live_sec_your_secret_key"`}
      />
      <InfoBox color="amber" title="Security rules">
        <ul className="list-disc pl-5 space-y-1 text-sm">
          <li>Never expose secret keys in HTML, JavaScript, or mobile apps</li>
          <li>Use HTTPS for all API calls</li>
          <li>Rotate keys immediately if exposed</li>
          <li>Never commit API keys to GitHub</li>
        </ul>
      </InfoBox>
    </Section>
  );
}

function ApiEndpointsSection() {
  return (
    <Section id="api-endpoints" title="API Endpoints">
      <p>Core Jjuma Pay v1 endpoints for payments, verification, transactions, and payment link lookup.</p>
      <div className="space-y-3">
        <EndpointCard
          method="POST" path={`${API_V1}/payments/create`}
          description="Create a hosted Jjuma checkout session. Returns a payment_url to redirect the customer."
          requiresKey={[{ label: 'Public Key', color: 'emerald' }, { label: 'Secret Key', color: 'violet' }]}
        />
        <EndpointCard
          method="GET" path={`${API_V1}/payments/verify/{transaction_id}`}
          description="Verify the status of a transaction. Secret key required."
          requiresKey={[{ label: 'Secret Key only', color: 'violet' }]}
        />
        <EndpointCard
          method="GET" path={`${API_V1}/transactions`}
          description="List your transactions with optional filters: ?status=successful&currency=UGX&page=1&limit=20"
          requiresKey={[{ label: 'Secret Key only', color: 'violet' }]}
        />
        <EndpointCard
          method="GET" path={`${API_V1}/payment-links/{slug}`}
          description="Look up a payment link by its slug. Public — no API key needed."
          requiresKey={[{ label: 'No key required', color: 'slate' }]}
        />
        <EndpointCard
          method="GET" path={`${API_V1}/currencies`}
          description="List all enabled currencies on the platform. Public — no key needed."
          requiresKey={[{ label: 'No key required', color: 'slate' }]}
        />
      </div>
    </Section>
  );
}

function CreatePaymentSection() {
  return (
    <Section id="create-payment" title="Create Payment" badge={<MethodBadge method="POST" />}>
      <p>
        Creates a hosted Jjuma checkout session on <strong>pay.jjuma.com</strong>.
        Redirect the customer to the returned <code className="bg-slate-100 px-1 rounded text-xs">payment_url</code>.
        Supports both JSON and HTML form submissions.
      </p>
      <div className="rounded-xl border border-slate-200 bg-slate-900 p-4">
        <p className="text-xs text-emerald-400 font-semibold font-mono mb-1">ENDPOINT</p>
        <code className="text-white font-mono text-sm">POST {API_V1}/payments/create</code>
      </div>
      <p className="font-semibold text-slate-800 mt-2">Request body fields:</p>
      <div className="rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-2.5 text-xs text-slate-500 font-semibold uppercase tracking-wide">Field</th>
              <th className="text-left px-4 py-2.5 text-xs text-slate-500 font-semibold uppercase tracking-wide">Type</th>
              <th className="text-left px-4 py-2.5 text-xs text-slate-500 font-semibold uppercase tracking-wide">Required</th>
              <th className="text-left px-4 py-2.5 text-xs text-slate-500 font-semibold uppercase tracking-wide">Description</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['amount',          'number',  'Yes', 'Payment amount (e.g. 50000)'],
              ['currency',        'string',  'Yes', 'ISO currency code (e.g. UGX, USD)'],
              ['description',     'string',  'Yes', 'Payment description shown on checkout'],
              ['customer_name',   'string',  'Yes', 'Customer full name'],
              ['customer_email',  'string',  'No',  'Customer email for receipt'],
              ['customer_phone',  'string',  'No',  'Customer phone number'],
              ['redirect_url',    'string',  'No',  'URL to redirect after payment'],
              ['webhook_url',     'string',  'No',  'URL to receive payment status webhook'],
              ['metadata',        'object',  'No',  'Your custom key-value data (e.g. order_id)'],
            ].map(([f, t, r, d]) => (
              <tr key={f} className="border-t border-slate-100 hover:bg-slate-50/50 transition-colors">
                <td className="px-4 py-2.5 font-mono text-xs text-emerald-700 font-medium">{f}</td>
                <td className="px-4 py-2.5 text-slate-500 text-xs">{t}</td>
                <td className="px-4 py-2.5">
                  {r === 'Yes'
                    ? <span className="text-[10px] font-bold text-red-600 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">Required</span>
                    : <span className="text-[10px] text-slate-400 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">Optional</span>}
                </td>
                <td className="px-4 py-2.5 text-slate-600 text-xs">{d}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CodeBlock
        lang="json"
        code={`POST ${API_V1}/payments/create\nAuthorization: Bearer bp_live_pub_your_public_key\n\n{\n  "amount": 50000,\n  "currency": "UGX",\n  "description": "Premium content access",\n  "customer_name": "John Doe",\n  "customer_email": "john@example.com",\n  "customer_phone": "+256701234567",\n  "redirect_url": "https://example.com/success",\n  "webhook_url": "https://example.com/webhooks/jjuma",\n  "metadata": { "order_id": "ORD-001" }\n}`}
      />
      <p className="font-semibold text-slate-800">Response:</p>
      <CodeBlock
        lang="json"
        code={`{\n  "status": "success",\n  "data": {\n    "transaction_id": "TXN-A1B2C3D4E5F6",\n    "reference": "REF-12345678",\n    "payment_url": "https://pay.jjuma.com/pay/TXN-A1B2C3D4E5F6",\n    "amount": 50000,\n    "currency": "UGX",\n    "status": "pending"\n  }\n}`}
      />
      <InfoBox color="amber">
        <p className="text-sm">When using an HTML <code className="bg-amber-100 px-1 rounded text-xs">{'<form>'}</code> POST, the server automatically redirects (303) to the <code className="bg-amber-100 px-1 rounded text-xs">payment_url</code> — no JavaScript needed.</p>
      </InfoBox>

      <p className="font-semibold text-slate-800 mt-4">Integration examples:</p>

      <div className="space-y-4">
        <div>
          <p className="text-xs font-semibold text-slate-700 mb-1 uppercase tracking-wider">A. HTML Form (no JavaScript)</p>
          <CodeBlock
            lang="html"
            code={`<form action="${API_V1}/payments/create" method="POST">\n  <input type="hidden" name="api_key" value="bp_live_pub_your_public_key" />\n  <input type="hidden" name="amount" value="50000" />\n  <input type="hidden" name="currency" value="UGX" />\n  <input type="hidden" name="description" value="Premium content access" />\n  <input type="hidden" name="customer_name" value="John Doe" />\n  <input type="hidden" name="customer_email" value="john@example.com" />\n  <input type="hidden" name="customer_phone" value="+256701234567" />\n  <input type="hidden" name="redirect_url" value="https://example.com/success" />\n  <input type="hidden" name="webhook_url" value="https://example.com/webhooks/jjuma" />\n  <button type="submit">Pay UGX 50,000 with Jjuma Pay</button>\n</form>`}
          />
        </div>

        <div>
          <p className="text-xs font-semibold text-slate-700 mb-1 uppercase tracking-wider">B. JavaScript fetch</p>
          <CodeBlock
            lang="javascript"
            code={`const response = await fetch("${API_V1}/payments/create", {\n  method: "POST",\n  headers: {\n    "Content-Type": "application/json",\n    "Authorization": "Bearer bp_live_pub_your_public_key"\n  },\n  body: JSON.stringify({\n    amount: 50000,\n    currency: "UGX",\n    description: "Premium content access",\n    customer_name: "John Doe",\n    customer_email: "john@example.com",\n    customer_phone: "+256701234567",\n    redirect_url: "https://example.com/success",\n    webhook_url: "https://example.com/webhooks/jjuma",\n    metadata: { order_id: "ORD-001" }\n  })\n});\n\nconst data = await response.json();\nif (data.status === "success" && data.data?.payment_url) {\n  window.location.href = data.data.payment_url;\n}`}
          />
        </div>

        <div>
          <p className="text-xs font-semibold text-slate-700 mb-1 uppercase tracking-wider">C. React component</p>
          <CodeBlock
            lang="jsx"
            code={`import { useState } from "react";\n\nexport default function JjumaPayButton({ orderId, amount }) {\n  const [loading, setLoading] = useState(false);\n\n  const handlePay = async () => {\n    setLoading(true);\n    try {\n      const res = await fetch("${API_V1}/payments/create", {\n        method: "POST",\n        headers: {\n          "Content-Type": "application/json",\n          "Authorization": "Bearer bp_live_pub_your_public_key",\n        },\n        body: JSON.stringify({\n          amount,\n          currency: "UGX",\n          description: "Order #" + orderId,\n          customer_name: "John Doe",\n          customer_email: "john@example.com",\n          redirect_url: "https://example.com/success",\n          metadata: { order_id: orderId },\n        }),\n      });\n      const data = await res.json();\n      if (data.status === "success" && data.data?.payment_url) {\n        window.location.href = data.data.payment_url;\n      }\n    } finally {\n      setLoading(false);\n    }\n  };\n\n  return (\n    <button onClick={handlePay} disabled={loading}>\n      {loading ? "Processing..." : \`Pay UGX \${amount.toLocaleString()}\`}\n    </button>\n  );\n}`}
          />
        </div>

        <div>
          <p className="text-xs font-semibold text-slate-700 mb-1 uppercase tracking-wider">D. PHP / cURL</p>
          <CodeBlock
            lang="php"
            code={`<?php\n$payload = [\n    "amount"         => 50000,\n    "currency"       => "UGX",\n    "description"    => "Premium content access",\n    "customer_name"  => "John Doe",\n    "customer_email" => "john@example.com",\n    "customer_phone" => "+256701234567",\n    "redirect_url"   => "https://example.com/success",\n    "webhook_url"    => "https://example.com/webhooks/jjuma",\n    "metadata"       => ["order_id" => "ORD-001"],\n];\n\n$ch = curl_init("${API_V1}/payments/create");\ncurl_setopt_array($ch, [\n    CURLOPT_RETURNTRANSFER => true,\n    CURLOPT_POST           => true,\n    CURLOPT_HTTPHEADER     => [\n        "Content-Type: application/json",\n        "Authorization: Bearer bp_live_pub_your_public_key",\n    ],\n    CURLOPT_POSTFIELDS     => json_encode($payload),\n]);\n\n$response = json_decode(curl_exec($ch), true);\ncurl_close($ch);\n\nif (!empty($response["data"]["payment_url"])) {\n    header("Location: " . $response["data"]["payment_url"]);\n    exit;\n}\n?>`}
          />
        </div>

        <div>
          <p className="text-xs font-semibold text-slate-700 mb-1 uppercase tracking-wider">E. Python requests</p>
          <CodeBlock
            lang="python"
            code={`import requests\n\nresponse = requests.post(\n    "${API_V1}/payments/create",\n    headers={"Authorization": "Bearer bp_live_pub_your_public_key"},\n    json={\n        "amount": 50000,\n        "currency": "UGX",\n        "description": "Premium content access",\n        "customer_name": "John Doe",\n        "customer_email": "john@example.com",\n        "redirect_url": "https://example.com/success",\n        "webhook_url": "https://example.com/webhooks/jjuma",\n        "metadata": {"order_id": "ORD-001"},\n    },\n)\n\ndata = response.json()\nif data.get("status") == "success":\n    print(data["data"]["payment_url"])  # redirect the user here`}
          />
        </div>

        <div>
          <p className="text-xs font-semibold text-slate-700 mb-1 uppercase tracking-wider">F. Node.js / Axios</p>
          <CodeBlock
            lang="javascript"
            code={`import axios from "axios";\n\nconst { data } = await axios.post(\n  "${API_V1}/payments/create",\n  {\n    amount: 50000,\n    currency: "UGX",\n    description: "Premium content access",\n    customer_name: "John Doe",\n    customer_email: "john@example.com",\n    redirect_url: "https://example.com/success",\n    webhook_url: "https://example.com/webhooks/jjuma",\n    metadata: { order_id: "ORD-001" },\n  },\n  { headers: { Authorization: "Bearer bp_live_pub_your_public_key" } }\n);\n\nif (data.status === "success") {\n  // Redirect customer to hosted checkout\n  window.location.href = data.data.payment_url;\n}`}
          />
        </div>

        <div>
          <p className="text-xs font-semibold text-slate-700 mb-1 uppercase tracking-wider">G. Laravel</p>
          <CodeBlock
            lang="php"
            code={`use Illuminate\\Support\\Facades\\Http;\n\n$response = Http::withToken('bp_live_pub_your_public_key')\n    ->post('${API_V1}/payments/create', [\n        'amount'        => 50000,\n        'currency'      => 'UGX',\n        'description'   => 'Premium content access',\n        'customer_name' => 'John Doe',\n        'redirect_url'  => 'https://example.com/success',\n        'webhook_url'   => 'https://example.com/webhooks/jjuma',\n        'metadata'      => ['order_id' => 'ORD-001'],\n    ]);\n\nif ($response->successful() && $response->json('status') === 'success') {\n    return redirect()->away($response->json('data.payment_url'));\n}`}
          />
        </div>

        <div>
          <p className="text-xs font-semibold text-slate-700 mb-1 uppercase tracking-wider">H. WordPress / WooCommerce</p>
          <CodeBlock
            lang="php"
            code={`$response = wp_remote_post("${API_V1}/payments/create", [\n    "headers" => [\n        "Content-Type"  => "application/json",\n        "Authorization" => "Bearer bp_live_pub_your_public_key",\n    ],\n    "body" => wp_json_encode([\n        "amount"        => $order->get_total(),\n        "currency"      => "UGX",\n        "description"   => "WooCommerce Order #" . $order->get_id(),\n        "customer_name" => $order->get_formatted_billing_full_name(),\n        "customer_email"=> $order->get_billing_email(),\n        "customer_phone"=> $order->get_billing_phone(),\n        "redirect_url"  => $order->get_checkout_order_received_url(),\n        "webhook_url"   => home_url("/webhooks/jjuma"),\n        "metadata"      => ["order_id" => $order->get_id(), "source" => "woocommerce"],\n    ]),\n]);\n\n$data = json_decode(wp_remote_retrieve_body($response), true);\nif (!empty($data["data"]["payment_url"])) {\n    wp_redirect($data["data"]["payment_url"]);\n    exit;\n}`}
          />
        </div>
      </div>
    </Section>
  );
}

function VerifyPaymentSection() {
  return (
    <Section id="verify-payment" title="Verify Payment" badge={<MethodBadge method="GET" />}>
      <p>
        Verify the status of a transaction using the <code className="bg-slate-100 px-1 rounded text-xs">transaction_id</code> returned
        when the payment was created. This endpoint requires a <strong>secret key</strong>.
      </p>
      <InfoBox color="amber">
        <p className="text-sm">⚠ Always verify payment status server-side before fulfilling orders. Never trust client-side callbacks alone.</p>
      </InfoBox>
      <div className="rounded-xl border border-slate-200 bg-slate-900 p-4">
        <p className="text-xs text-emerald-400 font-semibold font-mono mb-1">ENDPOINT</p>
        <code className="text-white font-mono text-sm">GET {API_V1}/payments/verify/{'{'}<span className="text-yellow-300">transaction_id</span>{'}'}</code>
      </div>
      <CodeBlock
        lang="cURL"
        code={`curl -X GET "${API_V1}/payments/verify/TXN-A1B2C3D4E5F6" \\\n  -H "Authorization: Bearer bp_live_sec_your_secret_key"`}
      />
      <p className="font-semibold text-slate-800">Response:</p>
      <CodeBlock
        lang="json"
        code={`{\n  "status": "success",\n  "data": {\n    "transaction_id": "TXN-A1B2C3D4E5F6",\n    "reference_code": "REF-12345678",\n    "status": "successful",\n    "amount": 50000,\n    "currency": "UGX",\n    "provider": "jjuma",\n    "customer_name": "John Doe",\n    "customer_email": "john@example.com",\n    "credited": true,\n    "created_at": "2024-01-01T12:00:00Z"\n  }\n}`}
      />
      <CodeBlock
        lang="javascript"
        code={`// Server-side verification (Node.js)\nconst res = await fetch(\n  \`${API_V1}/payments/verify/\${transaction_id}\`,\n  { headers: { Authorization: "Bearer bp_live_sec_your_secret_key" } }\n);\nconst { data } = await res.json();\n\nif (data.status === "successful") {\n  // Fulfill the order\n  await fulfillOrder(data.transaction_id);\n}`}
      />
    </Section>
  );
}

function TransactionsSection() {
  return (
    <Section id="transactions" title="List Transactions" badge={<MethodBadge method="GET" />}>
      <p>
        Returns a paginated list of your transactions. Requires a <strong>secret key</strong>.
      </p>
      <div className="rounded-xl border border-slate-200 bg-slate-900 p-4">
        <p className="text-xs text-emerald-400 font-semibold font-mono mb-1">ENDPOINT</p>
        <code className="text-white font-mono text-sm">GET {API_V1}/transactions</code>
      </div>
      <p className="font-semibold text-slate-800">Query parameters:</p>
      <div className="rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-2.5 text-xs text-slate-500 font-semibold uppercase">Parameter</th>
              <th className="text-left px-4 py-2.5 text-xs text-slate-500 font-semibold uppercase">Default</th>
              <th className="text-left px-4 py-2.5 text-xs text-slate-500 font-semibold uppercase">Description</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['page',     '1',           'Page number for pagination'],
              ['limit',    '20',          'Results per page (max 100)'],
              ['status',   'all',         'Filter: successful, pending, failed'],
              ['currency', 'all',         'Filter: UGX, USD, etc.'],
            ].map(([p, d, desc]) => (
              <tr key={p} className="border-t border-slate-100">
                <td className="px-4 py-2.5 font-mono text-xs text-emerald-700 font-medium">{p}</td>
                <td className="px-4 py-2.5 text-slate-500 text-xs">{d}</td>
                <td className="px-4 py-2.5 text-slate-600 text-xs">{desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CodeBlock
        lang="cURL"
        code={`curl -X GET "${API_V1}/transactions?status=successful&page=1&limit=20" \\\n  -H "Authorization: Bearer bp_live_sec_your_secret_key"`}
      />
      <p className="font-semibold text-slate-800">Response:</p>
      <CodeBlock
        lang="json"
        code={`{\n  "success": true,\n  "data": [\n    {\n      "transaction_id": "TXN-A1B2C3D4E5F6",\n      "reference_code": "REF-12345678",\n      "amount": 50000,\n      "currency": "UGX",\n      "status": "successful",\n      "provider": "jjuma",\n      "sender_name": "John Doe",\n      "credited": true,\n      "created_at": "2024-01-01T12:00:00Z"\n    }\n  ],\n  "total": 142,\n  "page": 1,\n  "limit": 20,\n  "pages": 8\n}`}
      />
    </Section>
  );
}

function PaymentLinksSection() {
  return (
    <Section id="payment-links" title="Payment Links" badge={<MethodBadge method="GET" />}>
      <p>
        Fetch a payment link by its slug. This endpoint is public — no API key required.
        Use it to display link details before sending the user to checkout.
      </p>
      <div className="rounded-xl border border-slate-200 bg-slate-900 p-4">
        <p className="text-xs text-emerald-400 font-semibold font-mono mb-1">ENDPOINT</p>
        <code className="text-white font-mono text-sm">GET {API_V1}/payment-links/{'{'}<span className="text-yellow-300">slug</span>{'}'}</code>
      </div>
      <CodeBlock
        lang="cURL"
        code={`curl "${API_V1}/payment-links/TXN-A1B2C3D4E5F6"`}
      />
      <p className="font-semibold text-slate-800">Response:</p>
      <CodeBlock
        lang="json"
        code={`{\n  "link": {\n    "slug": "TXN-A1B2C3D4E5F6",\n    "title": "School Fees Payment",\n    "description": "Term One school fees",\n    "fixed_amount": 500000,\n    "currency": "UGX",\n    "amount_type": "fixed",\n    "status": "active"\n  },\n  "owner": {\n    "name": "Jjuma Pay Merchant",\n    "verified": true,\n    "account_type": "business",\n    "profile_photo_url": "",\n    "public_support_email": "support@merchant.com"\n  }\n}`}
      />
      <p>Payment links can be fixed amount, donation-based, or custom amount. Share the link directly at <code className="bg-slate-100 px-1 rounded text-xs">pay.jjuma.com/pay/{'{slug}'}</code>.</p>
    </Section>
  );
}

function CheckoutSection() {
  return (
    <Section id="checkout" title="Checkout">
      <p>
        Hosted checkout runs at <strong>pay.jjuma.com</strong>. Customers complete payment directly
        without dashboard access. The page shows the merchant name, amount, description, and
        supported payment gateways.
      </p>
      <CodeBlock lang="text" code={`https://pay.jjuma.com/pay/{transaction_id}`} />
      <InfoBox color="slate" title="Checkout flow">
        <ol className="list-decimal pl-5 space-y-1.5 text-sm">
          <li>Customer is redirected to the <code className="text-xs bg-slate-200 px-1 rounded">payment_url</code></li>
          <li>Customer selects a payment method (MTN, Airtel, card, etc.)</li>
          <li>Payment is processed and verified by Jjuma Pay</li>
          <li>Webhook notification is sent to your <code className="text-xs bg-slate-200 px-1 rounded">webhook_url</code></li>
          <li>Customer is redirected to your <code className="text-xs bg-slate-200 px-1 rounded">redirect_url</code></li>
          <li>Transaction appears in your dashboard</li>
        </ol>
      </InfoBox>
    </Section>
  );
}

function WebhooksSection() {
  return (
    <Section id="webhooks" title="Webhooks">
      <p>
        Jjuma Pay sends a <code className="bg-slate-100 px-1 rounded text-xs">POST</code> request to your
        <code className="bg-slate-100 px-1 rounded text-xs"> webhook_url</code> whenever a payment status changes.
        Your endpoint must respond with HTTP <code className="bg-slate-100 px-1 rounded text-xs">200 OK</code>.
      </p>
      <p className="font-semibold text-slate-800">Webhook payload:</p>
      <CodeBlock
        lang="json"
        code={`{\n  "event": "payment.completed",\n  "data": {\n    "transaction_id": "TXN-A1B2C3D4E5F6",\n    "reference": "REF-12345678",\n    "amount": 50000,\n    "currency": "UGX",\n    "status": "successful",\n    "provider": "jjuma",\n    "customer": {\n      "name": "John Doe",\n      "email": "john@example.com",\n      "phone": "+256701234567"\n    },\n    "metadata": { "order_id": "ORD-001" }\n  }\n}`}
      />
      <p className="font-semibold text-slate-800">Handler examples:</p>
      <CodeBlock
        lang="PHP"
        code={`<?php\n$payload = json_decode(file_get_contents("php://input"), true);\n\nif ($payload["event"] === "payment.completed") {\n    $txn = $payload["data"];\n    // Verify the transaction independently before fulfilling\n    markOrderAsPaid($txn["metadata"]["order_id"]);\n    http_response_code(200);\n    echo "OK";\n    exit;\n}\nhttp_response_code(200); // Always return 200\n?>`}
      />
      <CodeBlock
        lang="javascript"
        code={`// Express.js webhook handler\napp.post("/webhooks/jjuma", express.json(), async (req, res) => {\n  const { event, data } = req.body;\n\n  if (event === "payment.completed") {\n    // Verify server-side before fulfilling\n    await verifyAndFulfill(data.transaction_id, data.metadata?.order_id);\n  }\n\n  // Always return 200 — even for events you don't handle\n  res.status(200).json({ received: true });\n});`}
      />
      <InfoBox color="slate" title="Webhook best practices">
        <ul className="list-disc pl-5 space-y-1 text-sm">
          <li>Always return <code className="text-xs bg-slate-200 px-1 rounded">200 OK</code> immediately, even for unhandled events</li>
          <li>Verify the transaction via <code className="text-xs bg-slate-200 px-1 rounded">GET /payments/verify/{'{id}'}</code> before fulfilling orders</li>
          <li>Store processed <code className="text-xs bg-slate-200 px-1 rounded">transaction_id</code> values to prevent duplicate processing</li>
          <li>Handle retries — Jjuma Pay may retry if your endpoint doesn't respond</li>
        </ul>
      </InfoBox>
    </Section>
  );
}

function WooCommerceSection() {
  return (
    <Section id="woocommerce" title="WooCommerce">
      <p>
        Integrate Jjuma Pay into WooCommerce by creating a custom payment gateway plugin that
        redirects customers to the hosted Jjuma checkout page.
      </p>
      <InfoBox color="slate" title="Configuration fields">
        <ul className="list-disc pl-5 space-y-1 text-sm">
          <li>API Base URL: <code className="text-xs bg-slate-200 px-1 rounded">https://api.jjuma.com</code></li>
          <li>Public API key (for creating payments)</li>
          <li>Secret API key (for verifying payments — store on server)</li>
          <li>Test / live mode toggle</li>
          <li>Webhook URL: <code className="text-xs bg-slate-200 px-1 rounded">https://yoursite.com/webhooks/jjuma</code></li>
          <li>Success URL (WooCommerce order received URL)</li>
        </ul>
      </InfoBox>
      <InfoBox color="emerald" title="Payment flow">
        <ol className="list-decimal pl-5 space-y-1.5 text-sm">
          <li>Customer places order and selects "Jjuma Pay"</li>
          <li>Your plugin calls <code className="text-xs bg-emerald-100 px-1 rounded">POST /payments/create</code></li>
          <li>Customer is redirected to Jjuma hosted checkout</li>
          <li>Customer completes payment on pay.jjuma.com</li>
          <li>Jjuma Pay sends a webhook to your site</li>
          <li>Your plugin verifies via <code className="text-xs bg-emerald-100 px-1 rounded">GET /payments/verify/{'{id}'}</code></li>
          <li>Order status updated to "Processing" or "Completed"</li>
        </ol>
      </InfoBox>
      <CodeBlock
        lang="php"
        code={`// WooCommerce process_payment() method\nfunction process_payment($order_id) {\n    $order = wc_get_order($order_id);\n\n    $response = wp_remote_post("${API_V1}/payments/create", [\n        "headers" => [\n            "Content-Type"  => "application/json",\n            "Authorization" => "Bearer " . $this->public_key,\n        ],\n        "body" => wp_json_encode([\n            "amount"        => $order->get_total(),\n            "currency"      => get_woocommerce_currency(),\n            "description"   => "WooCommerce Order #" . $order->get_id(),\n            "customer_name" => $order->get_formatted_billing_full_name(),\n            "customer_email"=> $order->get_billing_email(),\n            "customer_phone"=> $order->get_billing_phone(),\n            "redirect_url"  => $this->get_return_url($order),\n            "webhook_url"   => home_url("/webhooks/jjuma"),\n            "metadata"      => ["order_id" => $order_id, "source" => "woocommerce"],\n        ]),\n    ]);\n\n    $data = json_decode(wp_remote_retrieve_body($response), true);\n\n    if (!empty($data["data"]["payment_url"])) {\n        return ["result" => "success", "redirect" => $data["data"]["payment_url"]];\n    }\n\n    wc_add_notice("Payment failed. Please try again.", "error");\n    return ["result" => "fail"];\n}`}
      />
    </Section>
  );
}

function ErrorCodesSection() {
  return (
    <Section id="error-codes" title="Error Codes">
      <p>All errors return a JSON body with an <code className="bg-slate-100 px-1 rounded text-xs">error</code> message and optional <code className="bg-slate-100 px-1 rounded text-xs">code</code> field.</p>
      <div className="rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-2.5 text-xs text-slate-500 font-semibold uppercase tracking-wide">HTTP</th>
              <th className="text-left px-4 py-2.5 text-xs text-slate-500 font-semibold uppercase tracking-wide">Code</th>
              <th className="text-left px-4 py-2.5 text-xs text-slate-500 font-semibold uppercase tracking-wide">Meaning</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['400', 'BAD_REQUEST',           'Missing or invalid field (e.g. amount is 0, invalid email)'],
              ['401', 'INVALID_API_KEY',        'API key is missing, invalid, or revoked'],
              ['401', 'AUTH_REQUIRED',          'Authorization header is missing'],
              ['403', 'VERIFICATION_REQUIRED',  'Business account must be verified before API access'],
              ['403', 'ACCOUNT_NOT_APPROVED',   'Account is pending admin approval'],
              ['403', 'LIVE_MODE_DISABLED',     'Live mode not enabled for this account'],
              ['404', 'PAYMENT_LINK_NOT_FOUND', 'Payment link slug does not exist or is inactive'],
              ['404', 'TRANSACTION_NOT_FOUND',  'Transaction ID does not exist'],
              ['409', 'CONFLICT',               'Duplicate resource or conflicting state'],
              ['422', 'VALIDATION_ERROR',       'Field validation failed'],
              ['429', 'RATE_LIMIT_EXCEEDED',    'Too many requests — slow down'],
              ['500', 'SERVER_ERROR',            'Internal server error — contact support'],
            ].map(([code, err, msg]) => (
              <tr key={err} className="border-t border-slate-100 hover:bg-slate-50/50 transition-colors">
                <td className="px-4 py-2.5 font-mono text-xs font-semibold text-slate-700">{code}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-red-600">{err}</td>
                <td className="px-4 py-2.5 text-slate-600 text-xs">{msg}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="font-semibold text-slate-800">Example error response:</p>
      <CodeBlock
        lang="json"
        code={`{\n  "detail": "Your business account must be approved before you can create payments.",\n  "code": "ACCOUNT_NOT_APPROVED"\n}`}
      />
    </Section>
  );
}

function TestingSection() {
  return (
    <Section id="testing" title="Testing">
      <p>Use test API keys (<code className="bg-slate-100 px-1 rounded text-xs">bp_test_pub_...</code> / <code className="bg-slate-100 px-1 rounded text-xs">bp_test_sec_...</code>) to validate your integration before going live.</p>
      <InfoBox color="emerald" title="Test checklist">
        <ol className="list-decimal pl-5 space-y-1.5 text-sm">
          <li>Create a payment using test API keys</li>
          <li>Open the returned <code className="text-xs bg-emerald-100 px-1 rounded">payment_url</code> in your browser</li>
          <li>Complete a successful test payment</li>
          <li>Verify the transaction via <code className="text-xs bg-emerald-100 px-1 rounded">GET /payments/verify/{'{id}'}</code></li>
          <li>Confirm the webhook was delivered to your server</li>
          <li>Test a payment failure scenario</li>
          <li>Confirm transactions appear in your dashboard</li>
        </ol>
      </InfoBox>
      <InfoBox color="amber">
        <p className="text-sm">🧪 In test mode, no real money moves. Test transactions are separate from live transactions in the dashboard.</p>
      </InfoBox>
    </Section>
  );
}

function GoingLiveSection() {
  return (
    <Section id="going-live" title="Going Live">
      <p>Before switching to production, complete this checklist:</p>
      <InfoBox color="slate" title="Pre-launch checklist">
        <ol className="list-decimal pl-5 space-y-1.5 text-sm">
          <li>Business KYC completed and admin-approved</li>
          <li>Live mode enabled by admin on your account</li>
          <li>Generate live API keys (<code className="text-xs bg-slate-200 px-1 rounded">bp_live_pub_...</code> / <code className="text-xs bg-slate-200 px-1 rounded">bp_live_sec_...</code>)</li>
          <li>Replace test keys with live keys in your code</li>
          <li>Configure production webhook URL (HTTPS required)</li>
          <li>Test callback / redirect URLs in production</li>
          <li>Verify SSL certificate on your domain</li>
          <li>Add privacy policy and terms of service links</li>
          <li>Set a support email in your business profile</li>
          <li>Monitor the first 5–10 live transactions closely</li>
        </ol>
      </InfoBox>
      <InfoBox color="red">
        <p className="text-sm">🚨 Never use live keys in test/staging environments. Keep secret keys on the server only. Rotate keys immediately if exposed.</p>
      </InfoBox>
    </Section>
  );
}

/* ─── Sidebar ────────────────────────────────────────────────────────────── */
function DocsSidebar({ activeSection, mobileOpen, onClose, onNavigate }) {
  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={[
          'bg-white border border-slate-200 rounded-2xl shadow-sm',
          'fixed left-4 top-4 z-40 w-[min(86vw,300px)] max-h-[calc(100vh-2rem)] overflow-y-auto transition-transform duration-300 ease-out',
          'lg:static lg:z-auto lg:w-auto lg:max-h-[calc(100vh-6rem)] lg:sticky lg:top-24',
          mobileOpen ? 'translate-x-0' : '-translate-x-[110%] lg:translate-x-0',
        ].join(' ')}
      >
        <div className="p-5 space-y-4">
          {/* Brand */}
          <div className="flex items-start justify-between gap-3 lg:block">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-6 h-6 rounded-md bg-gradient-to-br from-emerald-600 to-emerald-500 flex items-center justify-center shadow-sm">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span className="text-xs font-semibold text-emerald-700 uppercase tracking-widest">Jjuma Pay</span>
              </div>
              <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Developer Docs
              </h2>
              <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                API reference, checkout integration &amp; webhooks
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="lg:hidden rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors flex-shrink-0"
            >
              ✕
            </button>
          </div>

          {/* Nav */}
          <nav className="space-y-0.5">
            {DOC_SECTIONS.map(([id, label]) => (
              <a
                key={id}
                href={`#${id}`}
                onClick={onNavigate}
                aria-current={activeSection === id ? 'page' : undefined}
                className={[
                  'flex items-center rounded-lg px-3 py-2 text-sm transition-all duration-150',
                  activeSection === id
                    ? 'bg-emerald-50 text-emerald-800 font-semibold border border-emerald-200/70'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 border border-transparent',
                ].join(' ')}
              >
                {activeSection === id && (
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-2 flex-shrink-0" />
                )}
                {label}
              </a>
            ))}
          </nav>

          {/* CTA */}
          <div className="pt-2 border-t border-slate-100 space-y-2">
            <a
              href="https://dashboard.jjuma.com/login"
              className="flex items-center justify-center rounded-xl bg-emerald-600 px-4 py-2.5 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors shadow-sm"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Open Dashboard →
            </a>
            <a
              href="https://dashboard.jjuma.com/register"
              className="flex items-center justify-center rounded-xl border border-slate-200 px-4 py-2 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors"
            >
              Create Account
            </a>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <button
          type="button"
          aria-label="Close documentation menu"
          onClick={onClose}
          className="fixed inset-0 z-30 bg-slate-900/40 backdrop-blur-sm lg:hidden"
        />
      )}
    </>
  );
}

/* ─── Main content area ──────────────────────────────────────────────────── */
function DocsContent({ onOpenMenu }) {
  return (
    <div className="space-y-2 min-w-0">
      {/* Mobile menu trigger */}
      <div className="lg:hidden sticky top-0 z-20 -mx-4 mb-4 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-gradient-to-br from-emerald-600 to-emerald-500 flex items-center justify-center">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
          <span className="text-sm font-bold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Developer Docs</span>
        </div>
        <button
          type="button"
          onClick={onOpenMenu}
          className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50 transition-colors"
        >
          ☰ Menu
        </button>
      </div>

      {/* Page header */}
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 mb-3">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          REST API · v1
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          API Documentation
        </h1>
        <p className="text-slate-500 text-sm mt-1.5 max-w-xl">
          Integrate Jjuma Pay into your website, mobile app, or e-commerce platform.
          Collect payments through hosted Jjuma checkout pages with just a few lines of code.
        </p>
      </div>

      {/* All sections */}
      <IntroductionSection />
      <GettingStartedSection />
      <AuthenticationSection />
      <ApiEndpointsSection />
      <CreatePaymentSection />
      <VerifyPaymentSection />
      <TransactionsSection />
      <PaymentLinksSection />
      <CheckoutSection />
      <WebhooksSection />
      <WooCommerceSection />
      <ErrorCodesSection />
      <TestingSection />
      <GoingLiveSection />
    </div>
  );
}

/* ─── Shell (standalone mode for doc.jjuma.com) ──────────────────────────── */
function DocsShell({ content, sidebar, setMobileOpen, activeSection, setActiveSection }) {
  const location = useLocation();

  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => { document.documentElement.style.scrollBehavior = ''; };
  }, []);

  useEffect(() => {
    const hash = (location.hash || window.location.hash || '').replace('#', '');
    if (!hash) return;
    const t = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) { el.scrollIntoView({ behavior: 'auto', block: 'start' }); setActiveSection(hash); }
    }, 0);
    return () => clearTimeout(t);
  }, [location.hash, setActiveSection]);

  useEffect(() => {
    const targets = DOC_SECTIONS.map(([id]) => document.getElementById(id)).filter(Boolean);
    if (!targets.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target?.id) setActiveSection(visible.target.id);
      },
      { rootMargin: '-16% 0px -60% 0px', threshold: [0.1, 0.25, 0.5] }
    );
    targets.forEach(t => observer.observe(t));
    return () => observer.disconnect();
  }, [setActiveSection]);

  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 1024) setMobileOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [setMobileOpen]);

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #f8fafc 50%, #f1f5f9 100%)' }}>
      {/* Top nav bar */}
      <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/90 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 h-14 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-500 flex items-center justify-center shadow-sm">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <span className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Jjuma Pay</span>
              <span className="ml-2 text-xs text-slate-400 font-medium">Developer Docs</span>
            </div>
          </a>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-bold text-emerald-700 tracking-wide">
              API v1
            </span>
            <a
              href="https://dashboard.jjuma.com/login"
              className="inline-flex items-center rounded-xl bg-emerald-600 px-4 py-2 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors shadow-sm"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Dashboard →
            </a>
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-4 py-8 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-8 items-start">
          {sidebar}
          <main className="min-w-0">
            {React.cloneElement(content, { onOpenMenu: () => setMobileOpen(true) })}
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white/70 mt-16">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-md bg-gradient-to-br from-emerald-600 to-emerald-500 flex items-center justify-center">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <span className="text-sm font-semibold text-slate-700" style={{ fontFamily: 'Outfit, sans-serif' }}>Jjuma Pay</span>
          </div>
          <p className="text-xs text-slate-400">© {new Date().getFullYear()} Jjuma Pay. All rights reserved.</p>
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <a href="https://dashboard.jjuma.com/register" className="hover:text-emerald-700 transition-colors">Sign Up</a>
            <a href="https://dashboard.jjuma.com/login" className="hover:text-emerald-700 transition-colors">Dashboard</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ─── Root export ────────────────────────────────────────────────────────── */
export default function Documentation({ standalone = false }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('introduction');

  const content = <DocsContent onOpenMenu={() => setMobileOpen(true)} />;
  const sidebar = (
    <DocsSidebar
      activeSection={activeSection}
      mobileOpen={standalone && mobileOpen}
      onClose={() => setMobileOpen(false)}
      onNavigate={() => { if (window.innerWidth < 1024) setMobileOpen(false); }}
    />
  );

  if (!standalone) return content;

  return (
    <DocsShell
      content={content}
      sidebar={sidebar}
      setMobileOpen={setMobileOpen}
      activeSection={activeSection}
      setActiveSection={setActiveSection}
    />
  );
}
